import React, { useState, useMemo, useCallback, useContext, useEffect } from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { Grid, Menu, MenuItem, IconButton } from '@material-ui/core';
import {
    Clear,
    MoreVert,
    StoreOutlined,
    WhereToVoteOutlined,
    DragHandle,
    AddCircle,
    RemoveCircle,
    Edit,
    CheckCircle,
    ReportProblemOutlined,
} from '@material-ui/icons';
import WarehouseOutlinedIcon from '@mui/icons-material/WarehouseOutlined';
import PushPin from '@mui/icons-material/PushPin';
import PushPinOutlined from '@mui/icons-material/PushPinOutlined';
import ArrowDropUpIcon from '@material-ui/icons/ArrowDropUp';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';

import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import { OnwardSwitch } from '@/styles/blocks';
import StickyNote2OutlinedIcon from '@mui/icons-material/StickyNote2Outlined';
import Typography from '@mui/material/Typography';
import Tooltip, { tooltipClasses } from '@mui/material/Tooltip';
import InfoIcon from '@material-ui/icons/Info';
import { useTotalCubes, useTotalWeight, useItemQuantity, useExchangeCubes } from '@/hooks';
import { poundFormatter, integerFormatter, minuteString, hourStringShort } from '@/constants/formats';
import routeStatusOptions from '@/constants/routeStatusOptions';
import { delWindowToFormattedDateRange, dateToFormattedDate } from '@/utilities/delWindowToFormattedDate';
import {
    calcDefaultServiceTime,
    getDispatchSettingPreference,
    stopExceedsCeiling,
} from '@/utilities/calculateTimeframes';
import { CONFIRMATION_SOURCE } from '@/constants/confirmationSource';
import { isOrderCancelled } from '../util/isOrderCancelled';
import { getStopConfirmation } from '../util/orderConfirmation';
import { getStopPinState, getUnpinCascadeIds } from '../util/pinnedStops';
import { PlanningUiContext } from '../context';
import { MODALS } from '../constants';
import { useClientUser } from '@/hooks/useClientUser';
import { captureException } from '@sentry/react';
import {
    Body1,
    Body2,
    circleStyle,
    ConfirmationText,
    greyText,
    GridItemRow,
    iconStyle,
    editiconStyle,
    PlanningListItem,
} from '../blocks';
import { colors } from '@/styles';
import { useMutation } from '@apollo/client';
import { UPDATE_ORDERS, UPDATE_ROUTE_STOPS, UPDATE_ROUTE, UPDATE_STOP_SCALARS } from '../graphql/mutations';
import {
    buildUpdateStopsOptimistic,
    buildStopScalarsOptimistic,
    updateStopsCacheHandler,
} from '../graphql/updateStopsOptimistic';
import { LOCATION_TYPES } from '@/constants/locationTypes';
import { cloneDeep } from 'lodash';
import { toArrayLiteral } from '@/utilities/toArrayLiteral';
import FTLStopHelpers from '@/utilities/FTLStopHelpers';
import { DISPATCH_PLAN_SERIALIZATION_KEY } from '@/graphql/serializingLink';
import { TIMEFRAME_CONFIRMATION_SUBSCRIPTION } from '../graphql/queries';
import { format } from 'date-fns';
import { PLAN_PAGE_REMOVE_WITHOUT_UNLOCK, PLAN_PAGE_SHOW_RATES } from '@/constants/featureFlags';
import { currencyFormatter } from '@/components/Claims/formatters';

const IconPlaceholder = styled.div`
    width: 20px;
    height: 20px;
`;

const IconPlaceholder24 = styled.div`
    width: 24px;
    height: 24px;
`;
const ItemTooltip = styled(({ className, ...props }) => <Tooltip {...props} classes={{ popper: className }} />)(
    ({ theme }) => ({
        [`& .${tooltipClasses.tooltip}`]: {
            backgroundColor: '#f5f5f9',
            color: 'rgba(0, 0, 0, 0.87)',
            maxWidth: 220,
            fontSize: 12,
            border: '1px solid #dadde9',
        },
    })
);

const StopListItem = ({
    stop,
    route,
    isLocked,
    subscribeToMore,
    resource,
    stopSequenceEnriched,
    onDragEnd,
    pushRouteToTracking,
}) => {
    const { circles, preferences_service_time_incr, dispatch_settings } = useClientUser();

    const [stopOrders, stopOrdersByKey] = useMemo(() => {
        const byKey = Object.fromEntries(
            (route?.orders || [])
                .filter((mapping) => (stop?.orders || []).includes(mapping.order_id))
                .map((mapping) => [mapping.order_id, mapping.order])
        );
        return [Object.values(byKey), byKey];
    }, [route, stop]);

    const preferences_service_time_incr_value = useMemo(() => {
        const firstOrder = stopOrders?.[0];
        if (!firstOrder) return preferences_service_time_incr;
        return (
            getDispatchSettingPreference('preferences_service_time_incr', dispatch_settings, firstOrder?.shipper_id) ??
            preferences_service_time_incr
        );
    }, [stopOrders, preferences_service_time_incr, dispatch_settings]);

    const windowExceedsCeiling = useMemo(
        () => stopExceedsCeiling(stop, stopOrdersByKey, dispatch_settings),
        [stop, stopOrdersByKey, dispatch_settings]
    );

    const [updateStops, { loading: stopsLoading }] = useMutation(UPDATE_ROUTE_STOPS, {
        // Stop/mapping lists self-heal from update_routes_by_pk's returning
        // snapshot for the ORDERED storage key. applyUpdateStopsResult also
        // evicts deleted stops and maintains the argument-less
        // stopsByRouteId key other pages read.
        context: { serializationKey: DISPATCH_PLAN_SERIALIZATION_KEY },
        update: updateStopsCacheHandler,
    });
    // Scalar-only edits (service time) — no update(): no deletes to evict, no
    // list snapshot to sync, since the stop entity normalizes on its own.
    const [updateStopScalars, { loading: scalarsLoading }] = useMutation(UPDATE_STOP_SCALARS, {
        context: { serializationKey: DISPATCH_PLAN_SERIALIZATION_KEY },
    });
    const [updateOrders] = useMutation(UPDATE_ORDERS);

    const [updateRoute, { loading: updateRouteLoading }] = useMutation(UPDATE_ROUTE, {
        onError: (e) => {
            console.error(e);
            captureException(e);
        },
    });

    const { setActionableRoute, setActionableStop, setActionableOrder, setModalOpen, setNotification, setError } =
        useContext(PlanningUiContext);

    const totalVolume = useTotalCubes(stopOrders);
    const totalWeight = useTotalWeight(stopOrders);
    const totalItems = useItemQuantity(stopOrders);
    const resourceCapacity = useMemo(() => {
        return resource?.capacity_cubes || null;
    }, [resource]);

    const truckCubes = useMemo(() => {
        let currentStop = stopSequenceEnriched.find((s) => s.ordering === stop.ordering);
        return currentStop && currentStop.totalTruckCubes ? currentStop.totalTruckCubes : 0;
    }, [stop, route, stopSequenceEnriched]);

    const serviceTime = useMemo(() => {
        // If the stop already has a service time, return that.
        if (stop.service_time === 0 || stop.service_time) {
            return stop.service_time;
        }

        const { hours, minutes } = calcDefaultServiceTime(stop, stopOrdersByKey, dispatch_settings);
        return ((hours || 0) * 60 + (minutes || 0)) * 60000;
    }, [stop, stopOrdersByKey]);

    const { status, label: statusLabel } = React.useMemo(() => getStopConfirmation(stopOrders), [stopOrders]);

    const inProgress = useMemo(() => {
        const ordersInProgress = (route.stopsByRouteId || []).reduce((acc, s) => {
            return [...acc, ...(s.stop_completion_time ? s.orders || [] : [])];
        }, []);
        return (stop.orders || []).some((order_id) => ordersInProgress.includes(order_id));
    }, [stop, route]);

    // Keyed on the order-id SET so route/stop identity churn (cache
    // broadcasts) doesn't tear down and re-open the websocket. Sorting only
    // affects the wire order of the `_in` list — server semantics identical.
    const stopOrderIdsKey = useMemo(() => Object.keys(stopOrdersByKey).sort().join('|'), [stopOrdersByKey]);

    useEffect(() => {
        let unsubscribe;

        unsubscribe = subscribeToMore({
            document: TIMEFRAME_CONFIRMATION_SUBSCRIPTION,
            variables: { order_ids: stopOrderIdsKey ? stopOrderIdsKey.split('|') : [] },
        });

        // Unsubscribe
        if (unsubscribe) return () => unsubscribe();
    }, [stopOrderIdsKey, subscribeToMore]);

    // cancelMap is built inside the memo so it survives re-renders where the
    // memo is skipped (it was previously a render-local mutated as a side
    // effect, which blanked the "(Cancelled)" badge on memo-skipped renders).
    const [allCanceled, cancelMap] = React.useMemo(() => {
        const map = {};
        const everyOrderCancelled = stopOrders.reduce((_allCancelled, _o) => {
            if (isOrderCancelled(_o)) {
                map[_o.order_number] = true;
                if (_allCancelled) return true;
            }
            return false;
        }, true);
        return [everyOrderCancelled, map];
    }, [stopOrders]);

    const modifyServiceTime = useCallback(
        (time, stop, route) => {
            let newServiceTime = serviceTime + time;
            if (newServiceTime < 0) newServiceTime = 0;

            const patchedStop = { ...stop, service_time: newServiceTime };

            updateStopScalars({
                variables: {
                    route_id: route.route_id,
                    route_update: {
                        need_to_optimize: true,
                    },
                    updates: [
                        {
                            where: { stop_id: { _eq: stop.stop_id } },
                            _set: {
                                service_time: newServiceTime,
                            },
                        },
                    ],
                },
                optimisticResponse: buildStopScalarsOptimistic({ route, stopsUpdated: [patchedStop] }),
                onError: (error) => {
                    setError(error, 'Error updating stop service time');
                },
            });
        },
        [serviceTime, stop]
    );

    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);
    const handleClick = (e) => {
        setAnchorEl(e.currentTarget);
        e.stopPropagation();
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const editTimeframe = () => {
        setModalOpen(MODALS.CUSTOM_TIMEFRAME);
        setActionableRoute(route);
        setActionableStop({ ...stop, stopOrders });
    };

    const handleAction = (e, option) => {
        setAnchorEl(null);
        e.stopPropagation();

        let status = 'pending';
        switch (option) {
            case 'Confirm timeframe':
                status = 'confirmed';
                break;
            case 'Reject timeframe':
                status = 'rejected';
                break;
            case 'Remove':
                return onRemoveStop();
            case 'Communications Log':
                setModalOpen(MODALS.COMMUNICATIONS);
                setActionableRoute(route);
                setActionableStop(stop);
                return;
            // Todo in future - re-add ability to add a note here if desired.
            // case 'Add Note':
            //     setModalOpen(MODALS.NOTES);
            //     setActionableRoute(route);
            //     setActionableStop(stop);
            //     return;
            default:
                status = 'pending';
        }

        updateOrders({
            variables: {
                order_ids: stop.orders,
                update: {
                    delivery_time_confirmed: status,
                    confirmation_source: status === 'confirmed' ? CONFIRMATION_SOURCE.MANUAL : null,
                },
            },
            onError: (error) => {
                setError(error, 'Error updating stop confirmation status');
            },
            onCompleted: () => {
                // If all other orders are confirmed, push route to tracking
                if (status === 'confirmed') {
                    const ordersByKey = Object.fromEntries(
                        (route.orders || []).map((mapping) => [mapping.order_id, mapping.order])
                    );
                    if (
                        (route.stopsByRouteId || []).every((s) => {
                            // skip this stop and other non customer stops
                            if (s.stop_id === stop.stop_id || !FTLStopHelpers.isCustomerStop(s, route)) {
                                return true;
                            }
                            return (s.orders || []).every(
                                (order_id) =>
                                    ordersByKey[order_id]?.order_status === 'cancelled' ||
                                    ordersByKey[order_id]?.delivery_time_confirmed === 'confirmed'
                            );
                        })
                    ) {
                        pushRouteToTracking(route, route.sending_to);
                    }
                }
            },
        });
    };

    const onRemoveStop = () => {
        if (stop.stop_completion_time) {
            setNotification({
                severity: 'warning',
                message: 'This stop has already been completed.',
            });
        } else if (inProgress) {
            setNotification({
                severity: 'warning',
                message:
                    'Orders have already been picked up and cannot be removed. Reschedule the delivery from the Mobile App or Tracking instead.',
            });
        } else if (route.status !== routeStatusOptions.PLANNING.value && !circles?.[PLAN_PAGE_REMOVE_WITHOUT_UNLOCK]) {
            setNotification({
                severity: 'warning',
                message:
                    'Cannot remove an order from a route that is in progress. If you would like to make edits, unlock the route first.',
            });
        } else {
            setModalOpen(MODALS.ROUTE_REMOVE_STOP);
            setActionableRoute(route);
            setActionableStop({ ...stop, stopOrders });
        }
    };

    const removeLunchStop = () => {
        const clonedStops = cloneDeep(route.stopsByRouteId);
        const idxOfLunch = clonedStops.findIndex((s) => s.type === 'LUNCH');
        const lunchStop = clonedStops.splice(idxOfLunch, 1)?.[0];

        const stopsToUpdate = clonedStops.filter((stop) => stop.ordering > lunchStop.ordering);
        const ordersThatNeedRecalculating = stopsToUpdate.reduce((acc, stop) => {
            return [...acc, ...(stop?.orders || [])];
        }, []);

        // Real arrays (not the toArrayLiteral strings sent in _set below) —
        // the server echoes real arrays for these columns.
        const patchedStops = stopsToUpdate.map((updatedStop) => ({
            ...updatedStop,
            ordering: updatedStop.ordering - 1,
        }));
        const patchedStopsById = Object.fromEntries(patchedStops.map((s) => [s.stop_id, s]));
        const recalcOrders = (route.orders || [])
            .filter((mapping) => ordersThatNeedRecalculating.includes(mapping.order_id))
            .map((mapping) => ({ ...mapping.order, delivery_time_confirmed: 'recalculate' }));

        updateStops({
            variables: {
                route_id: route.route_id,
                route_update: { need_to_optimize: true },
                order_updates: [
                    {
                        where: { order_id: { _in: ordersThatNeedRecalculating } },
                        _set: {
                            delivery_time_confirmed: 'recalculate',
                        },
                    },
                ],
                updates: stopsToUpdate.map((updatedStop) => ({
                    where: { stop_id: { _eq: updatedStop.stop_id } },
                    _set: {
                        ordering: updatedStop.ordering - 1,
                        orders: updatedStop.orders?.length ? toArrayLiteral(updatedStop.orders) : null,
                        returns: updatedStop.returns?.length ? toArrayLiteral(updatedStop.returns) : null,
                        exchanges: updatedStop.exchanges?.length ? toArrayLiteral(updatedStop.exchanges) : null,
                        ...Object.fromEntries(
                            FTLStopHelpers.stopFieldsToUpdate.map((key) => [key, updatedStop[key] || null])
                        ),
                    },
                })),
                deletes: lunchStop.stop_id,
            },
            optimisticResponse: {
                ...buildUpdateStopsOptimistic({
                    route,
                    stops: clonedStops
                        .map((s) => patchedStopsById[s.stop_id] || s)
                        .sort((a, b) => a.ordering - b.ordering),
                    stopsUpdated: patchedStops,
                    // Skip if a recalc order is missing from route.orders rather than
                    // write a partial OrderFields row (would flip the planning query
                    // partial and trigger a board-wide refetch flicker).
                    ordersUpdated: recalcOrders.length === ordersThatNeedRecalculating.length ? recalcOrders : [],
                }),
                delete_stops: { __typename: 'stops_mutation_response', affected_rows: 1, returning: [lunchStop] },
            },
            onError: (error) => {
                setError(error, 'Error removing lunch stop');
            },
        });
    };

    const options = useMemo(() => {
        const optional = isLocked && !circles?.[PLAN_PAGE_REMOVE_WITHOUT_UNLOCK] ? [] : ['Remove'];
        if (FTLStopHelpers.isCustomerStop(stop, route)) {
            if (isLocked) {
                return [...optional, 'Communications Log'];
            }
            return [
                ...(status !== 'confirmed' ? ['Confirm timeframe'] : []),
                ...(status !== 'rejected' ? ['Reject timeframe'] : []),
                ...optional,
                'Communications Log',
            ];
        }
        return optional;
    }, [stop, route, status, isLocked, inProgress]);

    // Pinning: a customer stop can be locked to the front of the route so
    // optimization keeps it (and any earlier pinned stops) in place. Pinned
    // stops must form a contiguous prefix — canPin/canUnpin enforce that.
    const pinState = useMemo(() => getStopPinState(stop, route), [stop, route]);

    const togglePin = useCallback(() => {
        const willPin = !stop.pinned;
        // Pinning affects just this stop. Unpinning cascades to every pinned
        // stop after it so the pinned set stays a contiguous prefix.
        const targetIds = willPin ? [stop.stop_id] : getUnpinCascadeIds(stop, route);

        const stopsById = Object.fromEntries((route.stopsByRouteId || []).map((s) => [s.stop_id, s]));
        const patchedStops = targetIds
            .map((id) => stopsById[id])
            .filter(Boolean)
            .map((s) => ({ ...s, pinned: willPin }));

        updateStopScalars({
            variables: {
                route_id: route.route_id,
                route_update: { need_to_optimize: true },
                updates: targetIds.map((id) => ({
                    where: { stop_id: { _eq: id } },
                    _set: { pinned: willPin },
                })),
            },
            optimisticResponse: buildStopScalarsOptimistic({ route, stopsUpdated: patchedStops }),
            onError: (error) => {
                setError(error, 'Error updating stop pin');
            },
        });
    }, [stop, route]);

    const isFinalDropoff = useMemo(() => {
        return stop.type === 'DROPOFF' && stop.returns?.length;
    }, [stop]);
    const isNonFinalDropoff = useMemo(() => {
        return stop.type === 'DROPOFF' && !stop.returns?.length;
    }, [stop]);
    const isExchangeDropoff = useMemo(() => {
        return stop.type === 'DROPOFF' && stop.exchanges?.length;
    }, [stop]);
    const isNormalDropoff = useMemo(() => {
        return stop.type === 'DROPOFF' && !stop.returns?.length && !stop.exchanges?.length;
    }, [stop]);

    const isReturnPickup = useMemo(() => {
        return stop.type === 'PICKUP' && stop.returns?.length;
    }, [stop]);
    const isNormalPickup = useMemo(() => {
        return stop.type === 'PICKUP' && !stop.returns?.length;
    }, [stop]);
    const isLunchStop = useMemo(() => {
        return stop.type === 'LUNCH';
    }, [stop]);

    const orderLocationTypes = stopOrders.reduce((types, order) => {
        const type = order.order_type === 'return' ? order.pickup_location_type : order.dropoff_location_type;
        if (type && !types.includes(type)) {
            return [...types, type];
        }
        return types;
    }, []);

    const poNumbers = stopOrders.filter((order) => order.po_number).map((order) => order.po_number);

    const isClaimed = stopOrders?.some((order) => !order.oms);

    const revenueTotals = useMemo(() => {
        if (stop.type === 'PICKUP') return { carrierRevenue: 0, shipperRevenue: 0 };
        let carrierRevenue = 0;
        let shipperRevenue = 0;
        stopOrders.forEach((order) => {
            if (order.oms) {
                carrierRevenue += order.order_revenue || 0;
            } else {
                shipperRevenue += order.shipper_rate || 0;
                carrierRevenue += order.carrier_rate || 0;
            }
        });
        return {
            carrierRevenue,
            shipperRevenue: circles?.[PLAN_PAGE_SHOW_RATES] ? shipperRevenue : 0,
        };
    }, [stopOrders, circles, stop.type]);

    const stopListItemHeader = useMemo(() => {
        if (isLunchStop) {
            return `${stop.ordering}. Lunch`;
        }

        let name = 'Stop';
        if (FTLStopHelpers.isCustomerStop(stop, route)) {
            name = stopOrders[0]?.dropoff_name || 'Customer Name';
        } else if (FTLStopHelpers.isCrossdockStop(stop, route)) {
            name = 'Cross-dock';
        } else if (isFinalDropoff) {
            name = 'Final Return';
        } else if (stop.type === 'PICKUP') {
            name = 'Pickup';
        } else if (stop.type === 'DROPOFF') {
            name = 'Dropoff';
        }
        return `${stop.ordering}. ${name}`;
    }, [stop, route, stopOrders, isLunchStop, isFinalDropoff]);

    const serviceTimeText = useMemo(() => {
        if (isLunchStop) {
            return `${serviceTime / 60000} min`;
        }
        return `${isNormalPickup ? 'Load' : 'Service'} Time: ${minuteString.format(serviceTime / 60000)}`;
    }, [serviceTime, isLunchStop, isNormalPickup]);

    const setOvernight = (stop_id, newValue) => {
        const ordersToRecalculate = (route?.orders || [])
            .map((mapping) => mapping.order)
            .reduce((toRecal, o) => {
                const orderStop = route?.stopsByRouteId?.find((s) => {
                    return s?.orders?.includes(o.order_id) && !FTLStopHelpers.isWarehousePickup(s);
                });
                if (o?.delivery_time_confirmed && orderStop.ordering > stop.ordering) {
                    return [...toRecal, o.order_id];
                }
                return toRecal;
            }, []);

        const patchedStop = {
            ...stop,
            del_window_end: null,
            del_window_start: null,
            stop_start_time: null,
            stop_end_time: null,
            overnight: newValue,
        };
        const recalcOrders = (route.orders || [])
            .filter((mapping) => ordersToRecalculate.includes(mapping.order_id))
            .map((mapping) => ({ ...mapping.order, delivery_time_confirmed: 'recalculate' }));

        updateStops({
            variables: {
                route_id: route.route_id,
                route_update: {
                    need_to_optimize: true,
                },
                order_updates: [
                    {
                        where: { order_id: { _in: ordersToRecalculate } },
                        _set: {
                            delivery_time_confirmed: 'recalculate',
                        },
                    },
                ],
                updates: [
                    {
                        where: { stop_id: { _in: [stop_id] } },
                        _set: {
                            del_window_end: null,
                            del_window_start: null,
                            stop_start_time: null,
                            stop_end_time: null,
                            overnight: newValue,
                        },
                    },
                ],
            },
            optimisticResponse: buildUpdateStopsOptimistic({
                route,
                stops: (route.stopsByRouteId || []).map((s) => (s.stop_id === stop_id ? patchedStop : s)),
                stopsUpdated: [patchedStop],
                // Skip if a recalc order is missing from route.orders rather than
                // write a partial OrderFields row (would flip the planning query
                // partial and trigger a board-wide refetch flicker).
                ordersUpdated: recalcOrders.length === ordersToRecalculate.length ? recalcOrders : [],
            }),
            onError: (error) => {
                setError(error, 'Error changing route to overnight');
            },
        });
    };

    return (
        <PlanningListItem
            selected={open}
            css={css`
                flex-wrap: nowrap !important;
                flex-direction: row;
            `}
        >
            {!isLocked && (
                <Grid
                    item
                    css={css`
                        display: flex;
                        align-items: center;
                        margin-right: 1rem !important;
                        flex-direction: column;
                        align-self: center;
                        gap: 5px;
                    `}
                >
                    {!isFinalDropoff && stop.ordering > 0 ? (
                        <IconButton
                            css={css`
                                padding: 0;
                            `}
                            onClick={(e) => {
                                const dragObject = {
                                    destination: {
                                        droppableId: 'planning-stops',
                                        index: stop.ordering - 1,
                                    },
                                    source: {
                                        droppableId: 'planning-stops',
                                        index: stop.ordering,
                                    },
                                    draggableId: stop.stop_id,
                                };
                                onDragEnd(dragObject);
                                e.stopPropagation();
                                e.preventDefault();
                            }}
                        >
                            <ArrowDropUpIcon fontSize="small" />
                        </IconButton>
                    ) : (
                        <IconPlaceholder />
                    )}
                    {!isFinalDropoff ? <DragHandle fontSize="small" /> : <IconPlaceholder />}
                    {!isFinalDropoff && stop.ordering < route.stopsByRouteId.length - 1 ? (
                        <IconButton
                            css={css`
                                padding: 0;
                            `}
                            onClick={(e) => {
                                const dragObject = {
                                    destination: {
                                        droppableId: 'planning-stops',
                                        index: stop.ordering + 1,
                                    },
                                    source: {
                                        droppableId: 'planning-stops',
                                        index: stop.ordering,
                                    },
                                    draggableId: stop.stop_id,
                                };
                                onDragEnd(dragObject);
                                e.stopPropagation();
                                e.preventDefault();
                            }}
                        >
                            <ArrowDropDownIcon fontSize="small" />
                        </IconButton>
                    ) : (
                        <IconPlaceholder />
                    )}
                </Grid>
            )}
            <Grid
                item
                css={css`
                    display: flex;
                    flex-direction: column;
                    flex-grow: 1;
                `}
            >
                {/* Stop # + Customer Name / Pickup / Final Return + Icons/Options */}
                <GridItemRow>
                    <Body1>
                        {stopListItemHeader}{' '}
                        {isClaimed && (
                            <Body2
                                css={css`
                                    color: ${colors.greens.primary};
                                    font-style: italic;
                                `}
                            >
                                (Marketplace)
                            </Body2>
                        )}
                    </Body1>
                    <Grid
                        item
                        css={css`
                            display: flex;
                        `}
                    >
                        {isLunchStop ? (
                            <IconButton
                                onClick={removeLunchStop}
                                css={css`
                                    padding: 0 !important;
                                `}
                            >
                                <Clear css={iconStyle} />
                            </IconButton>
                        ) : (
                            <>
                                {pinState.isPinnable && (
                                    <Tooltip
                                        title={
                                            pinState.isPinned
                                                ? 'Pinned to front of route. Click to unpin (also unpins any stops pinned after it).'
                                                : pinState.canPin
                                                ? 'Pin this stop to the front of the route'
                                                : 'Pin the preceding stop first'
                                        }
                                    >
                                        <span>
                                            <IconButton
                                                onClick={togglePin}
                                                disabled={
                                                    isLocked ||
                                                    scalarsLoading ||
                                                    (!pinState.canPin && !pinState.canUnpin)
                                                }
                                                css={css`
                                                    padding: 0 !important;
                                                `}
                                            >
                                                {pinState.isPinned ? (
                                                    <PushPin
                                                        css={css`
                                                            color: ${colors.greens.primary};
                                                            font-size: 20px;
                                                        `}
                                                    />
                                                ) : (
                                                    <PushPinOutlined css={iconStyle} />
                                                )}
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                )}
                                {FTLStopHelpers.isCrossdockStop(stop, route) ? (
                                    <WarehouseOutlinedIcon css={iconStyle} />
                                ) : isNormalPickup || isFinalDropoff ? (
                                    <StoreOutlined css={iconStyle} />
                                ) : (
                                    <WhereToVoteOutlined css={iconStyle} />
                                )}
                                <IconButton
                                    onClick={handleClick}
                                    css={css`
                                        padding: 0 !important;
                                    `}
                                >
                                    <MoreVert css={iconStyle} />
                                </IconButton>
                                <Menu
                                    anchorEl={anchorEl}
                                    open={open}
                                    onClose={handleClose}
                                    css={css`
                                        inset: 0px calc(1rem + 50px) !important;
                                    `}
                                >
                                    {options.map((option) => (
                                        <MenuItem
                                            key={option}
                                            disabled={stopsLoading || scalarsLoading || updateRouteLoading}
                                            onClick={(e) => handleAction(e, option)}
                                        >
                                            <Body2>{option}</Body2>
                                        </MenuItem>
                                    ))}
                                </Menu>
                            </>
                        )}
                    </Grid>
                </GridItemRow>

                {/* Order Type + Delivery Type */}
                {FTLStopHelpers.isCustomerStop(stop, route) ? (
                    <>
                        <GridItemRow
                            css={css`
                                justify-content: space-between;
                            `}
                        >
                            <Body2>
                                {isExchangeDropoff ? 'Exchange' : isReturnPickup ? 'Return Pickup' : 'Delivery'}{' '}
                                <span css={greyText}>
                                    -{' '}
                                    {orderLocationTypes
                                        .map((location) => LOCATION_TYPES[location] || location)
                                        .join(', ')}
                                </span>
                            </Body2>
                            {revenueTotals.shipperRevenue > 0 && (
                                <Body2
                                    css={css`
                                        display: flex;
                                        align-items: center;
                                        gap: 4px;
                                        color: ${colors.greys[2]};
                                        white-space: nowrap;
                                        min-width: 76px;
                                        justify-content: space-between;
                                    `}
                                >
                                    <Tooltip title="Shipper rate for this stop.">
                                        <InfoOutlinedIcon fontSize="small" />
                                    </Tooltip>
                                    {currencyFormatter(revenueTotals.shipperRevenue)}
                                </Body2>
                            )}
                        </GridItemRow>
                        <GridItemRow>
                            {/* <Body2 css={greyText}>
                                {orderLocationTypes.map((location) => LOCATION_TYPES[location] || location).join(', ')}
                            </Body2> */}
                        </GridItemRow>
                    </>
                ) : null}

                {stop.stop_completion_time ? (
                    <GridItemRow
                        css={css`
                            justify-content: space-between;
                        `}
                    >
                        <Body2
                            css={css`
                                color: ${colors.greens.primary};
                                font-weight: 700;
                            `}
                        >
                            Completed: {format(new Date(stop.stop_completion_time), 'EEE, MMM d @ hh:mm a')}
                        </Body2>
                        {revenueTotals.carrierRevenue > 0 && (
                            <Body2
                                css={css`
                                    display: flex;
                                    align-items: center;
                                    gap: 4px;
                                    color: ${colors.greys[2]};
                                    white-space: nowrap;
                                    min-width: 76px;
                                    justify-content: space-between;
                                `}
                            >
                                <Tooltip title="Carrier rate for this stop.">
                                    <InfoOutlinedIcon fontSize="small" />
                                </Tooltip>
                                {currencyFormatter(revenueTotals.carrierRevenue)}
                            </Body2>
                        )}
                    </GridItemRow>
                ) : null}

                {/* Driving Time */}
                {!isNormalPickup && !isLunchStop && stop.driving_time && (
                    <GridItemRow
                        css={css`
                            justify-content: space-between;
                        `}
                    >
                        <Body2>
                            {`Driving Time: ${
                                stop.driving_time >= 3600000
                                    ? hourStringShort.format(Math.floor(stop.driving_time / 3600000))
                                    : ''
                            } ${minuteString.format((stop.driving_time % 3600000) / 60000)}`}
                        </Body2>
                        {!stop.stop_completion_time && revenueTotals.carrierRevenue > 0 && (
                            <Body2
                                css={css`
                                    display: flex;
                                    align-items: center;
                                    gap: 4px;
                                    color: ${colors.greys[2]};
                                    white-space: nowrap;
                                    min-width: 76px;
                                    justify-content: space-between;
                                `}
                            >
                                <Tooltip title="Carrier rate for this stop.">
                                    <InfoOutlinedIcon fontSize="small" />
                                </Tooltip>
                                {currencyFormatter(revenueTotals.carrierRevenue)}
                            </Body2>
                        )}
                    </GridItemRow>
                )}

                {!isLunchStop && (
                    <GridItemRow>
                        <Body2>{stop.address}</Body2>
                    </GridItemRow>
                )}

                {/* Arrival Time */}
                {!isLunchStop && (stop.stop_start_time || stop.del_window_start) && (
                    <GridItemRow>
                        <Body2>
                            {`Arrival Time: ${dateToFormattedDate(
                                stop.stop_start_time || stop.del_window_start,
                                stopOrders[0]?.pickup_zip
                            )}`}
                        </Body2>
                    </GridItemRow>
                )}

                {/* Load / Service Time */}
                {!(stop.overnight && isNormalPickup) && (
                    <GridItemRow>
                        <Body2>{serviceTimeText}</Body2>
                        {!isLocked && !isLunchStop && (
                            <Grid
                                item
                                css={css`
                                    display: flex;
                                    align-items: center;
                                `}
                            >
                                <IconButton
                                    onClick={() =>
                                        modifyServiceTime(preferences_service_time_incr_value * -60000, stop, route)
                                    }
                                    css={css`
                                        padding: 0;
                                    `}
                                >
                                    <RemoveCircle css={circleStyle} />
                                </IconButton>
                                <Body1
                                    css={css`
                                        padding: 0 0.25rem;
                                    `}
                                >
                                    {minuteString.format(preferences_service_time_incr_value)}
                                </Body1>
                                <IconButton
                                    onClick={() =>
                                        modifyServiceTime(preferences_service_time_incr_value * 60000, stop, route)
                                    }
                                    css={css`
                                        padding: 0;
                                    `}
                                >
                                    <AddCircle css={circleStyle} />
                                </IconButton>
                            </Grid>
                        )}
                    </GridItemRow>
                )}

                {/* Dispatch / End Time */}
                {!isLunchStop && (stop.stop_start_time || stop.del_window_start) && (
                    <GridItemRow>
                        <Body2>
                            {`${isNormalPickup ? 'Dispatch' : 'End'} Time:
                                ${dateToFormattedDate(
                                    new Date(
                                        new Date(stop.stop_start_time || stop.del_window_start).getTime() + serviceTime
                                    ),
                                    stopOrders[0]?.pickup_zip
                                )}
                            `}
                        </Body2>
                    </GridItemRow>
                )}

                {/* Items / Cubes / Pounds */}
                {!isLunchStop && (
                    <GridItemRow>
                        <Body2>
                            {[
                                `${integerFormatter.format(totalItems)} items`,
                                `${integerFormatter.format(totalVolume)} cu ft.`,
                                poundFormatter.format(totalWeight),
                            ].join(' / ')}
                            <ItemTooltip
                                title={
                                    <>
                                        <Typography
                                            css={css`
                                                font-size: 14 !important;
                                                font-weight: 600 !important;
                                            `}
                                            color="inherit"
                                        >
                                            Item Information
                                        </Typography>
                                        {stopOrders &&
                                            stopOrders?.map((order) =>
                                                order.itemsByOrderId.length ? (
                                                    order.itemsByOrderId.map((item) => (
                                                        <React.Fragment key={item.item_id}>
                                                            {item.quantity} x {item.description || 'No description'}
                                                            <br />
                                                        </React.Fragment>
                                                    ))
                                                ) : (
                                                    <React.Fragment>
                                                        No items found <br />
                                                    </React.Fragment>
                                                )
                                            )}
                                    </>
                                }
                            >
                                <InfoIcon css={editiconStyle} fontSize="small" />
                            </ItemTooltip>

                            {window.innerWidth > 1599 ? (
                                <Body2
                                    style={{
                                        color: resourceCapacity && truckCubes > resourceCapacity ? 'red' : 'grey',
                                        fontSize: 12,
                                        fontStyle: 'italic',
                                    }}
                                >
                                    {' '}
                                    (Cubes on Truck: {integerFormatter.format(truckCubes)})
                                </Body2>
                            ) : (
                                <>
                                    <br />
                                    <Body2
                                        style={{
                                            color: resourceCapacity && truckCubes > resourceCapacity ? 'red' : 'grey',
                                            fontSize: 12,
                                            fontStyle: 'italic',
                                        }}
                                    >
                                        {' '}
                                        (Cubes on Truck: {integerFormatter.format(truckCubes)})
                                    </Body2>
                                </>
                            )}
                        </Body2>
                    </GridItemRow>
                )}

                {/* Timeframe */}
                {!isNormalPickup && stop.del_window_start && stop.del_window_end && (
                    <GridItemRow>
                        <Body2
                            css={
                                windowExceedsCeiling &&
                                css`
                                    color: ${colors.oranges.primary};
                                    font-weight: 600;
                                `
                            }
                        >
                            {'Time Frame: '}
                            {delWindowToFormattedDateRange(
                                stop.del_window_start,
                                stop.del_window_end,
                                stopOrders[0]?.dropoff_zip
                            )}
                            {status && FTLStopHelpers.isCustomerStop(stop, route) && (
                                <ConfirmationText status={status}> ({statusLabel})</ConfirmationText>
                            )}
                            {!isLocked && circles['custom-timeframes-beta'] && (
                                <IconButton
                                    onClick={editTimeframe}
                                    css={css`
                                        padding: 0 !important;
                                    `}
                                >
                                    <Edit css={editiconStyle} fontSize="small" />
                                </IconButton>
                            )}
                            {windowExceedsCeiling && (
                                <Tooltip
                                    title="This delivery window ends past your configured ceiling."
                                    placement="top"
                                    arrow
                                >
                                    <ReportProblemOutlined
                                        css={css`
                                            color: ${colors.oranges.primary};
                                            font-size: 16px;
                                            vertical-align: text-bottom;
                                            margin-left: 4px;
                                        `}
                                    />
                                </Tooltip>
                            )}
                        </Body2>
                    </GridItemRow>
                )}

                {(stop.type === 'LUNCH' || FTLStopHelpers.isCustomerStop(stop, route)) && (
                    <GridItemRow>
                        <Body2
                            css={css`
                                color: ${colors.greys.primary};
                                cursor: pointer;
                                text-decoration: underline;
                            `}
                            onClick={() => {
                                setModalOpen(MODALS.STOP_WINDOW);
                                setActionableRoute(route);
                                setActionableStop(stop);
                            }}
                        >
                            {'Optimized Window: '}
                            {stop.earliest_start && stop.latest_end
                                ? delWindowToFormattedDateRange(
                                      stop.earliest_start,
                                      stop.latest_end,
                                      stopOrders?.[0]?.dropoff_zip
                                  )
                                : stop.type === 'LUNCH'
                                ? 'NOT SET'
                                : 'No Preference'}
                        </Body2>
                    </GridItemRow>
                )}

                {/* Order Numbers */}
                {!isNormalPickup && (
                    <GridItemRow
                        css={css`
                            justify-content: flex-start;
                            flex-wrap: wrap;
                        `}
                    >
                        {stopOrders.map((order, idx) => (
                            <React.Fragment key={order.order_id}>
                                <Body2
                                    css={css`
                                        color: ${colors.greens.primary};
                                        text-decoration: underline;
                                        cursor: pointer;
                                        font-weight: 600;
                                    `}
                                    onClick={() => {
                                        setModalOpen(MODALS.ORDER_VIEW);
                                        setActionableOrder(order);
                                    }}
                                >
                                    {order.order_number}
                                </Body2>
                                {!allCanceled && cancelMap[order.order_number] && (
                                    <Body2
                                        css={css`
                                            color: ${colors.reds.primary};
                                        `}
                                    >
                                        &nbsp;(Cancelled)
                                    </Body2>
                                )}
                                {idx < stopOrders.length - 1 && <Body2 css={greyText}>,&nbsp;</Body2>}
                            </React.Fragment>
                        ))}
                    </GridItemRow>
                )}

                {/* PO Numbers */}
                {poNumbers.length > 0 && (
                    <GridItemRow>
                        <Body2>{poNumbers.join(', ')}</Body2>
                    </GridItemRow>
                )}

                {isFinalDropoff ? (
                    <GridItemRow
                        css={css`
                            justify-content: flex-start;
                        `}
                    >
                        <OnwardSwitch
                            label={
                                <span
                                    css={css`
                                        font-size: 14px;
                                        font-weight: 500;
                                    `}
                                >
                                    Next Day Return
                                </span>
                            }
                            checked={route?.finish_returns_next_day}
                            onChange={(e) =>
                                updateRoute({
                                    variables: {
                                        route_id: route.route_id,
                                        update: {
                                            finish_returns_next_day: !route.finish_returns_next_day,
                                            need_to_optimize: true,
                                        },
                                    },
                                    optimisticResponse: {
                                        update_routes_by_pk: {
                                            ...route,
                                            finish_returns_next_day: !route.finish_returns_next_day,
                                            need_to_optimize: true,
                                        },
                                    },
                                })
                            }
                            disabled={stopsLoading || scalarsLoading || updateRouteLoading || isLocked}
                        />
                        <ItemTooltip
                            title={
                                <>
                                    <Typography
                                        css={css`
                                            font-size: 14px !important;
                                            font-weight: 500 !important;
                                            line-height: 17px;
                                        `}
                                        color="inherit"
                                    >
                                        Final return will be scheduled for the next day. This will move the end location
                                        prior to the final return and be the starting point to finish the return the
                                        following day.
                                    </Typography>
                                </>
                            }
                            placement="top"
                            arrow
                            css={css`
                                .MuiTooltip-arrow {
                                    // color: #f5f5f9;
                                }
                            `}
                        >
                            <InfoOutlinedIcon
                                css={css`
                                    color: ${colors.greens.primary};
                                    margin-left: 5px !important;
                                `}
                                style={{ fontSize: 20 }}
                            />
                        </ItemTooltip>
                    </GridItemRow>
                ) : null}

                {circles?.['overnight-routes'] && isNormalPickup && (
                    <GridItemRow
                        css={css`
                            justify-content: flex-start;
                        `}
                    >
                        <OnwardSwitch
                            label={
                                <span
                                    css={css`
                                        font-size: 14px;
                                        font-weight: 500;
                                    `}
                                >
                                    Overnight Stop
                                </span>
                            }
                            checked={stop.overnight}
                            onChange={(e) => setOvernight(stop.stop_id, !stop.overnight)}
                            disabled={stopsLoading || scalarsLoading}
                        />
                        <ItemTooltip
                            title={
                                <>
                                    <Typography
                                        css={css`
                                            font-size: 14px !important;
                                            font-weight: 500 !important;
                                            line-height: 17px;
                                        `}
                                        color="inherit"
                                    >
                                        This pickup stop will be loaded the day before delivery. This stop will be
                                        skipped for navigation and optimization. Enter an overnight location as Start
                                        Location for correct driver navigation.
                                    </Typography>
                                </>
                            }
                            placement="top"
                            arrow
                            css={css`
                                .MuiTooltip-arrow {
                                    // color: #f5f5f9;
                                }
                            `}
                        >
                            <InfoOutlinedIcon
                                css={css`
                                    color: ${colors.greens.primary};
                                    margin-left: 5px !important;
                                `}
                                style={{ fontSize: 20 }}
                            />
                        </ItemTooltip>
                    </GridItemRow>
                )}
            </Grid>
        </PlanningListItem>
    );
};

export default React.memo(StopListItem);
